import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_LOCALE,
  STORAGE_KEY,
  normalizeLocale,
  type Locale,
} from "./config";
import en, { type Dict } from "./locales/en";
import es from "./locales/es";
import ptBr from "./locales/pt-br";
import fr from "./locales/fr";
import de from "./locales/de";
import it from "./locales/it";
import ru from "./locales/ru";

const DICTS: Record<Locale, Dict> = {
  en,
  es,
  "pt-br": ptBr,
  fr,
  de,
  it,
  ru,
};

type Vars = Record<string, string | number>;

interface I18nContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  /** Resolve a dotted key to a string, with English fallback + {var} interpolation. */
  t: (path: string, vars?: Vars) => string;
  /** Full resolved dictionary for the active locale (arrays/objects). */
  dict: Dict;
}

function resolve(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k) =>
    k in vars ? String(vars[k]) : m,
  );
}

/** English-based default so components render (in English) even without a provider (e.g. unit tests). */
const DEFAULT_VALUE: I18nContextValue = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (path: string, vars?: Vars) => {
    const v = resolve(en, path);
    return typeof v === "string" ? interpolate(v, vars) : path;
  },
  dict: en,
};

const I18nContext = createContext<I18nContextValue>(DEFAULT_VALUE);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === "undefined") return DEFAULT_LOCALE;
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
  });

  // Keep <html lang> in sync for accessibility / SEO.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", locale === "pt-br" ? "pt-BR" : locale);
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    const normalized = normalizeLocale(next);
    setLocaleState(normalized);
    try {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      /* storage may be unavailable (private mode) — in-memory switch still works */
    }
  }, []);

  const dict = DICTS[locale] ?? en;

  const t = useCallback(
    (path: string, vars?: Vars): string => {
      const hit = resolve(dict, path);
      const value = typeof hit === "string" ? hit : resolve(en, path);
      if (typeof value !== "string") return path;
      return interpolate(value, vars);
    },
    [dict],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, dict }),
    [locale, setLocale, t, dict],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Convenience hook when only the translation function is needed. */
export function useT() {
  return useI18n().t;
}
